import * as React from 'react';

import '@ant-design/compatible/assets/index.css';
import { Modal, Select, Form, Row, Col, Empty, Alert, notification } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import {
	spendCategoriesRestCrudThunksForBuyer,
	spendCategoriesRestCrudThunksForSupplier,
} from '../../thunks/spend_categories_thunks';
import {
	fetchLocationsWithRegionsForBuyer,
	fetchLocationsWithRegionsForSupplier,
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
	problemTypesRestCrudThunksForBuyer,
	problemTypesRestCrudThunksForSupplier,
} from '../../thunks/problem_types_thunks';

import { LocationDropdownRecord } from '../name_component/location/LocationDropdownRecord';
import { useState } from 'react';
import {
	copyRankForSuppliersToLocationsForBuyer,
	copyRankForSuppliersToLocationsForSupplier,
	getSupplierPrivateNetworksForLocationForBuyer,
	getSupplierPrivateNetworksForLocationForSupplier,
} from '../../thunks/supplier_private_networks_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import { ROLE_TYPES } from '../../utils/DataConstants';

const FormItem = Form.Item;

function CopyRankingComponent(props) {
	const [form] = Form.useForm();
	const {
		selectedLocation,
		locations,
		visible,
		onCancel,
		copyRankings,
		fetchLocations,
		onSuccess,
		problemType = {},
		problemTypeIds = [],
		fetchSupplierPrivateNetworksForLocation,
	} = props;

	// At the location level (no single `problemType`), respect any active problem
	// type filter: copy only the filtered problem types instead of every one.
	const hasProblemTypeFilter = !problemType.id && problemTypeIds.length > 0;

	const onSubmit = (values) => {
		setConfirmLoading(true);
		// Single problem type (per-problem-type copy) → just that one; otherwise scope
		// to the active filter; undefined tells the backend to copy every problem type.
		const problemTypeIdsToCopy = problemType.id
			? [problemType.id]
			: hasProblemTypeFilter
			? problemTypeIds
			: undefined;
		// "Copy from another location" reads from the user-selected location and writes
		// into the location being viewed — i.e. a single-destination case of the batch copy.
		const toLocationIds = [selectedLocation.id];
		copyRankings({
			fromLocationId: values.locationId,
			toLocationIds,
			problemTypeIds: problemTypeIdsToCopy,
		})
			.then((result) => {
				const failureCount = nullSafeGetOrElse('failureCount', result, 0);
				if (failureCount > 0) {
					notification.warning({
						message: 'Some locations could not be updated',
						description: `Supplier assignments were copied to ${nullSafeGetOrElse(
							'successCount',
							result,
							0
						)} of ${toLocationIds.length} locations. ${failureCount} failed.`,
					});
				} else {
					notification.success({
						message: 'Supplier assignments copied',
						description: `Copied to ${toLocationIds.length} location${
							toLocationIds.length === 1 ? '' : 's'
						}.`,
					});
				}
				onSuccess();
			})
			.catch((errorMessage) => {
				notification.error({
					message: 'Unable to copy supplier assignments',
					description: errorMessage || 'Something went wrong. Please try again.',
				});
			})
			.finally(() => {
				setConfirmLoading(false);
			});
	};

	const [confirmLoading, setConfirmLoading] = useState(false);
	if (!visible) return null;
	return (
		<Modal
			visible={visible}
			width={700}
			okText="Next"
			onCancel={onCancel}
			onOk={() => {
				form.validateFields(['locationId']).then(() => {
					if (problemType.hierarchyName) {
						setConfirmLoading(true);
						fetchSupplierPrivateNetworksForLocation(form.getFieldValue('locationId'), {
							problemTypeId: problemType.id,
							offset: 0,
							limit: 10,
						}).then(({ data, count }) => {
							const { supplierPrivateNetworkSeq, problemType } = data[0] || {};
							const newRankings = JSON.parse(
								JSON.stringify(
									supplierPrivateNetworkSeq.sort((a, b) => {
										return a.rank - b.rank;
									})
								)
							);

							Modal.confirm({
								width: 1000,
								title: (
									<div>
										<span>
											Copy supplier assignments from
											<b> {locations.records[form.getFieldValue('locationId')].name}</b> to
											<b> {selectedLocation.name}</b>
											{problemType.hierarchyName ? (
												<span>
													{' '}
													for <i>{problemType.hierarchyName}</i>
												</span>
											) : (
												''
											)}
										</span>
									</div>
								),
								content: (
									<Row style={{ height: '50vh', overflow: 'scroll' }}>
										<Col span={24}>
											<div className=" my-4 text-base  font-semibold">
												New rankings would be as follows
											</div>
											<div className="PrivateNetworkLocationsPage__secondaryEntity__list__container">
												{(newRankings &&
													newRankings.length &&
													newRankings.map((item, index) => (
														<div className="flex  items-center justify-start p-4">
															<div className="flex basis-2/4 items-center">
																<div
																	title={nullSafeGet('supplierFacility.displayName', item)}
																	className=" PrivateNetworkLocationsPage__secondaryEntity__list__item__text line-clamp-1 text-ellipsis"
																>
																	{nullSafeGet('supplierFacility.displayName', item)}
																</div>
															</div>
															<div className="ml-16 basis-2/4 opacity-50">{`Rank ${
																index + 1
															}`}</div>
														</div>
													))) || (
													<div className="py-4">
														<Empty
															image={Empty.PRESENTED_IMAGE_SIMPLE}
															description={
																'You haven’t assigned any service providers to this problem type yet'
															}
														/>
													</div>
												)}
											</div>
										</Col>
									</Row>
								),
								onOk() {
									form.submit();
								},
								onCancel() {
									setConfirmLoading(false);
								},
								okText: 'Confirm',
							});
						});
					} else {
						const fromLocationId = form.getFieldValue('locationId');
						const locationsRecords = nullSafeGetOrElse('records', locations, {});
						const fromLocationName = nullSafeGet('name', locationsRecords[fromLocationId]);
						Modal.confirm({
							width: 520,
							title: (
								<span className="flex items-start">
									<span className="max-w-[70%] break-words" title={fromLocationName}>
										{fromLocationName}
									</span>
									<ArrowRightOutlined translate="" className="mx-2 mt-0.5 text-gray-400" />
									<span className="max-w-[70%] break-words" title={selectedLocation.name}>
										{selectedLocation.name}
									</span>
								</span>
							),
							content: (
								<div>
									This will overwrite supplier assignments at <b>{selectedLocation.name}</b> for{' '}
									{hasProblemTypeFilter ? 'the selected' : 'all'} problem types.
								</div>
							),
							onOk() {
								form.submit();
							},
							onCancel() {
								setConfirmLoading(false);
							},
							okText: 'Confirm',
						});
					}
				});
			}}
			confirmLoading={confirmLoading}
			title={
				<span>
					Copy supplier assignments to {selectedLocation.name}
					{problemType.hierarchyName ? (
						<span>
							{' '}
							for <i>{problemType.hierarchyName}</i>
						</span>
					) : (
						''
					)}
				</span>
			}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				initialValues={{}}
				onFinish={onSubmit}
			>
				{!problemType.id ? (
					<Alert
						type="info"
						showIcon
						className="!mb-6"
						message={
							hasProblemTypeFilter ? (
								<div>
									<div className="mb-2">
										Only the following filtered problem types will be copied:
									</div>
									<ProblemTypeHierarchicalDisplay
										problemTypeIds={problemTypeIds}
										displayMode="pill"
									/>
								</div>
							) : (
								'All problem types will be copied to this location.'
							)
						}
					/>
				) : null}
				<div className="mb-6 flex items-start gap-3">
					<FormItem
						className="mb-0 min-w-[50%] flex-1"
						name="locationId"
						label="Copy from"
						required
						rules={[{ required: true, message: 'Please select a location' }]}
					>
						<OWAsyncSelect
							emptyWhenDisabled={false}
							stateSlice={locations}
							targetCollectionName="SUPPLIER_PRIVATE_NETWORK_FORM_LOCATIONS_AUTOCOMPLETE"
							fetchMultiple={(ids, targetCollectionName) => {}}
							fetchData={(
								searchText,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							) => {
								fetchLocations(
									{ name: searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
							}}
							renderRecord={(record) => (
								<Select.Option
									disabled={record.id === selectedLocation.id}
									key={record.id}
									value={record.id}
								>
									<LocationDropdownRecord record={record} />
								</Select.Option>
							)}
							sortBy={{ sort_by: 'name', order: 'ascend' }}
						/>
					</FormItem>
					<FormItem className="mb-0" label={<span>&nbsp;</span>} colon={false}>
						<ArrowRightOutlined translate="" className="text-lg text-gray-400" />
					</FormItem>
					<FormItem className="mb-0 min-w-[20%]" label="Copy to">
						<Select
							disabled
							className="w-full"
							value={selectedLocation.name}
							options={[{ label: selectedLocation.name, value: selectedLocation.name }]}
						/>
					</FormItem>
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	supplierFacilitiesFetching: state.supplier_facilities.fetching,
	spendCategories: state.spend_categories,
	problemTypes: state.problem_types,
	locations: state.locations,
	createErrors: state.supplier_private_networks.createErrors,
	updateErrors: state.supplier_private_networks.updateErrors,
	creating: state.supplier_private_networks.creating,
	updating: state.supplier_private_networks.updating,
	supplierPrivateNetworkFetching: state.supplier_private_networks.fetching,
	supplierPrivateNetworkByFacilityFetching:
		state.supplier_private_networks.supplierPrivateNetworkBySupplierFacility.fetching,
	supplierPrivateNetworkByFacilityRecords:
		state.supplier_private_networks.supplierPrivateNetworkBySupplierFacility.records,

	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getSupplierFacility: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readOne(id)
				: supplierFacilitiesRestCrudThunksForBuyer.readOne(id)
		),
	fetchSpendCategories: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? spendCategoriesRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: spendCategoriesRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleSpendCategories: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? spendCategoriesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: spendCategoriesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: problemTypesHeirarchicalForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: locationsRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchLocationsWithHierarchy: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchLocationsWithRegionsForSupplier(
						{ ...params, noIsActiveFilter: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: fetchLocationsWithRegionsForBuyer(
						{ ...params, noIsActiveFilter: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	copyRankings: ({ fromLocationId, toLocationIds, problemTypeIds }) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? copyRankForSuppliersToLocationsForSupplier({
						fromLocationId,
						toLocationIds,
						problemTypeIds,
				  })
				: copyRankForSuppliersToLocationsForBuyer({ fromLocationId, toLocationIds, problemTypeIds })
		),
	fetchSupplierPrivateNetworksForLocation: (locationId, params) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? getSupplierPrivateNetworksForLocationForSupplier(locationId, params)
				: getSupplierPrivateNetworksForLocationForBuyer(locationId, params)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(CopyRankingComponent)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
